import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Pencil, Trash2, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import MemberDialog from "@/components/team/MemberDialog";
import EditMemberDialog from "@/components/team/EditMemberDialog";

function MemberRow({ m, me, onChange, onReset, onRemove, onEdit }) {
  const self = m.id === me.id;
  return (
    <li className="flex flex-col sm:flex-row sm:items-center gap-4 py-5 border-b border-[#103080]/10 last:border-0" data-testid={`team-member-${m.id}`}>
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className="w-11 h-11 rounded-full bg-[#103080]/[0.07] text-[#103080] font-serif-brand font-semibold flex items-center justify-center shrink-0">
          {m.name.slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-[#103080] truncate">{m.name} {self && <span className="text-xs text-[#0F172A]/40 font-normal">(you)</span>}</p>
          <p className="text-sm text-[#0F172A]/55 truncate">{m.email}</p>
        </div>
      </div>
      <span className={`text-xs font-semibold rounded-full px-3 py-1 w-fit capitalize ${m.role === "admin" ? "bg-[#103080] text-white" : "bg-[#F5F2EB] text-[#103080]"}`}>{m.role}</span>
      <label className="flex items-center gap-2 text-sm text-[#0F172A]/70">
        <Switch checked={m.active} disabled={self} onCheckedChange={(v) => onChange(m, { active: v })} data-testid={`team-member-active-${m.id}`} />
        {m.active ? "Approved" : "Paused"}
      </label>
      <div className="flex items-center gap-2">
        <button onClick={() => onEdit(m)} data-testid={`team-member-edit-${m.id}`} aria-label="Edit member"
          className="w-9 h-9 rounded-full border border-[#103080]/15 text-[#103080] flex items-center justify-center hover:bg-[#103080] hover:text-white transition-colors duration-200"><Pencil size={14} /></button>
        <button onClick={() => onReset(m)} data-testid={`team-member-reset-${m.id}`} aria-label="Reset password"
          className="w-9 h-9 rounded-full border border-[#103080]/15 text-[#103080] flex items-center justify-center hover:bg-[#103080] hover:text-white transition-colors duration-200"><KeyRound size={14} /></button>
        {!self && (
          <button onClick={() => onRemove(m)} data-testid={`team-member-remove-${m.id}`} aria-label="Remove member"
            className="w-9 h-9 rounded-full border border-red-200 text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors duration-200"><Trash2 size={14} /></button>
        )}
      </div>
    </li>
  );
}

export default function Team() {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/team").then((r) => setTeam(r.data)).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
  }, []);

  const change = async (m, patch) => {
    try {
      await api.patch(`/team/${m.id}`, patch);
      toast.success(patch.password ? "Password reset" : "Team member updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name}? Their tasks and blocked time will be deleted.`)) return;
    try {
      await api.delete(`/team/${m.id}`);
      toast.success("Team member removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="max-w-4xl space-y-8 fade-up" data-testid="team-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-semibold mb-3">Team access</p>
          <h1 className="font-serif-brand font-semibold text-[#103080] text-4xl sm:text-5xl">Who can <i className="text-[#0090D0]">sign in</i></h1>
          <p className="mt-3 text-[#0F172A]/60 max-w-lg">Only people you add here can access the portal. Pause someone to block access without deleting their work.</p>
        </div>
        <button onClick={() => setDialog({ mode: "add" })} data-testid="team-add-member-button"
          className="inline-flex items-center gap-2 rounded-full bg-[#103080] text-white font-medium px-6 h-11 text-sm hover:bg-[#0090D0] transition-colors duration-200 shrink-0">
          <UserPlus size={16} /> Add team member
        </button>
      </div>
      <ul className="rounded-2xl border border-[#103080]/10 bg-white/70 ambient px-6" data-testid="team-list">
        {team.map((m) => (
          <MemberRow key={m.id} m={m} me={user} onChange={change} onReset={(x) => setDialog({ mode: "reset", member: x })} onRemove={remove} onEdit={setEditing} />
        ))}
      </ul>
      <MemberDialog state={dialog} onClose={() => setDialog(null)} onDone={load} onReset={change} />
      {editing && <EditMemberDialog member={editing} isSelf={editing.id === user.id} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
